/**
 * Legacy Bootstrap: Splash Server Constants
 * =========================================
 *
 * Old code uses the `splash.server.constants` namespace for server-generated feature flags.
 * This provides an interface for safely accessing those and detecting changes.
 *
 * We want to ensure that this namespace is READ-ONLY so that we can migrate to the new bootstrap
 * entirely when it's possible.
 */

import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import union from 'lodash/union';

import { flag } from 'utils/flags';

/**
 * Grab a fresh copy of the constants so we can know whether things change later
 */
const constants = cloneDeep(get(window, 'splash.server.constants', {}));

/**
 * Get a constant from the legacy boostrap
 *
 * @param  {string} path - name of the constant to get (or path, if it's deep)
 * @param  {*} [fallback] - value to use if the path is not set
 * @return {*} the value
 */
export const constant = (path, fallback = null, forceRefresh = flag('readOnlyConstants')) =>
    forceRefresh ? get(window, `splash.server.constants.${path}`, fallback) : get(constants, path, fallback);

/**
 * Get a constant from the legacy boostrap as a boolean
 *
 * @param  {string} path - name of the constant to get (or path, if it's deep)
 * @return {boolean}
 */
export const bool = (path) => !!constant(path);

/**
 * I'm not sure when/how we modify splash.server.constants after the page loads.
 * Exposing this on local to compare until I'm confident that we can use the initial cloned data
 * every time we read a constant.
 */
if (__DEVELOPMENT__) {
    const getDirtyKeys = (a, b) => {
        let hasDirt = false;
        const dirtyKeys = {};
        union(Object.keys(a), Object.keys(b)).forEach((key) => {
            const valueA = a[key];
            const valueB = b[key];
            if (valueA !== valueB) {
                if (typeof valueA === 'object' && typeof valueB === 'object') {
                    const deeperDirt = getDirtyKeys(valueA, valueB);
                    if (deeperDirt != null) {
                        dirtyKeys[key] = deeperDirt;
                        hasDirt = true;
                    }
                } else {
                    dirtyKeys[key] = valueB;
                    hasDirt = true;
                }
            }
        });
        return hasDirt ? dirtyKeys : null;
    };
    window.getInitialConstants = () => constants;
    window.getDirtyConstants = () => getDirtyKeys(constants, get(window, 'splash.server.constants', {}));
}
